import { useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { updateMyProfile } from "@/services/profile";
import { ChoiceCard, OnboardingQuestion, SelectionSection } from "./OnboardingQuestion";
import { US_REGIONS, US_STATES } from "./locations";
import type { OnboardingStepProps } from "./types";

const DIVISION_OPTIONS = [
  {
    value: "D1",
    title: "NCAA Division I",
    description: "Large programs and athletic budgets",
    icon: "D1",
  },
  {
    value: "D2",
    title: "NCAA Division II",
    description: "Competitive golf with more balance",
    icon: "D2",
  },
  {
    value: "D3",
    title: "NCAA Division III",
    description: "Strong academics and competitive golf",
    icon: "D3",
  },
  {
    value: "NAIA",
    title: "NAIA",
    description: "Smaller schools with recruiting flexibility",
    icon: "NAIA",
  },
  {
    value: "NJCAA",
    title: "Junior college",
    description: "A two-year path to develop and transfer",
    icon: "JUCO",
  },
] as const;

const NEARBY: Record<string, string[]> = {
  AL: ["FL", "GA", "MS", "TN"],
  AK: [],
  AZ: ["CA", "CO", "NM", "NV", "UT"],
  AR: ["LA", "MO", "MS", "OK", "TN", "TX"],
  CA: ["AZ", "NV", "OR"],
  CO: ["AZ", "KS", "NE", "NM", "OK", "UT", "WY"],
  CT: ["MA", "NY", "RI"],
  DE: ["MD", "NJ", "PA"],
  DC: ["MD", "VA"],
  FL: ["AL", "GA"],
  GA: ["AL", "FL", "NC", "SC", "TN"],
  HI: [],
  ID: ["MT", "NV", "OR", "UT", "WA", "WY"],
  IL: ["IA", "IN", "KY", "MO", "WI"],
  IN: ["IL", "KY", "MI", "OH"],
  IA: ["IL", "MN", "MO", "NE", "SD", "WI"],
  KS: ["CO", "MO", "NE", "OK"],
  KY: ["IL", "IN", "MO", "OH", "TN", "VA", "WV"],
  LA: ["AR", "MS", "TX"],
  ME: ["NH"],
  MD: ["DC", "DE", "PA", "VA", "WV"],
  MA: ["CT", "NH", "NY", "RI", "VT"],
  MI: ["IN", "OH", "WI"],
  MN: ["IA", "ND", "SD", "WI"],
  MS: ["AL", "AR", "LA", "TN"],
  MO: ["AR", "IA", "IL", "KS", "KY", "NE", "OK", "TN"],
  MT: ["ID", "ND", "SD", "WY"],
  NE: ["CO", "IA", "KS", "MO", "SD", "WY"],
  NV: ["AZ", "CA", "ID", "OR", "UT"],
  NH: ["MA", "ME", "VT"],
  NJ: ["DE", "NY", "PA"],
  NM: ["AZ", "CO", "OK", "TX", "UT"],
  NY: ["CT", "MA", "NJ", "PA", "VT"],
  NC: ["GA", "SC", "TN", "VA"],
  ND: ["MN", "MT", "SD"],
  OH: ["IN", "KY", "MI", "PA", "WV"],
  OK: ["AR", "CO", "KS", "MO", "NM", "TX"],
  OR: ["CA", "ID", "NV", "WA"],
  PA: ["DE", "MD", "NJ", "NY", "OH", "WV"],
  RI: ["CT", "MA"],
  SC: ["GA", "NC"],
  SD: ["IA", "MN", "MT", "ND", "NE", "WY"],
  TN: ["AL", "AR", "GA", "KY", "MO", "MS", "NC", "VA"],
  TX: ["AR", "LA", "NM", "OK"],
  UT: ["AZ", "CO", "ID", "NV", "WY"],
  VT: ["MA", "NH", "NY"],
  VA: ["DC", "KY", "MD", "NC", "TN", "WV"],
  WA: ["ID", "OR"],
  WV: ["KY", "MD", "OH", "PA", "VA"],
  WI: ["IA", "IL", "MI", "MN"],
  WY: ["CO", "ID", "MT", "NE", "SD", "UT"],
};

type LocationMode = "anywhere" | "home" | "nearby" | "regions" | "specific";
function StatePicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div>
      {value.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {value.map(code => (
            <span
              key={code}
              className="inline-flex items-center gap-1.5 rounded-full bg-lime-soft px-3 py-1.5 text-xs font-bold text-forest"
            >
              {code}
              <button
                type="button"
                aria-label={`Remove ${code}`}
                onClick={() => onChange(value.filter(item => item !== code))}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <button
            type="button"
            onKeyDown={event => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                setOpen(true);
              }
            }}
            className="flex h-12 w-full items-center justify-between rounded-xl border border-border-strong bg-white px-4 text-sm text-forest"
          >
            {value.length
              ? `${value.length} state${value.length === 1 ? "" : "s"} selected`
              : "Search and choose states"}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
          onOpenAutoFocus={event => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <Command>
            <CommandInput ref={inputRef} placeholder="Search states…" />
            <CommandList>
              <CommandEmpty>No state found.</CommandEmpty>
              <CommandGroup>
                {US_STATES.map(state => (
                  <CommandItem
                    key={state.code}
                    value={`${state.name} ${state.code}`}
                    onSelect={() =>
                      onChange(
                        value.includes(state.code)
                          ? value.filter(item => item !== state.code)
                          : [...value, state.code]
                      )
                    }
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value.includes(state.code) ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {state.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function PreferencesStep({
  profile,
  onValidityChange,
  registerSave,
  updateProfile,
}: OnboardingStepProps) {
  const [divisions, setDivisions] = useState<string[]>(
    profile.divisionsOpenTo?.length
      ? profile.divisionsOpenTo
      : DIVISION_OPTIONS.map(option => option.value)
  );
  const [locationMode, setLocationMode] = useState<LocationMode>(
    profile.locationPreferenceMode ??
      (profile.preferredRegions?.length
        ? "regions"
        : profile.preferredStates?.length
          ? "specific"
          : "anywhere")
  );
  const [states, setStates] = useState<string[]>(profile.preferredStates ?? []);
  const [regions, setRegions] = useState<string[]>(profile.preferredRegions ?? []);
  const [maxAnnualNetPrice, setMaxAnnualNetPrice] = useState(profile.maxAnnualNetPrice ?? 0);
  const [notes, setNotes] = useState(profile.schoolPreferencesNotes ?? "");

  useEffect(() => onValidityChange(true), [onValidityChange]);
  const valuesRef = useRef({ divisions, locationMode, states, regions, maxAnnualNetPrice, notes });
  valuesRef.current = { divisions, locationMode, states, regions, maxAnnualNetPrice, notes };

  useEffect(() => {
    registerSave(async () => {
      const values = valuesRef.current;
      const priceLimit = values.maxAnnualNetPrice > 0 ? values.maxAnnualNetPrice : null;
      try {
        await updateMyProfile({
          divisionsOpenTo: values.divisions,
          locationPreferenceMode: values.locationMode,
          preferredStates: values.states,
          preferredRegions: values.regions,
          maxAnnualNetPrice: priceLimit,
          schoolPreferencesNotes: values.notes.trim() || null,
        });
        updateProfile?.({
          preferredStates: values.states,
          preferredRegions: values.regions,
          locationPreferenceMode: values.locationMode,
          maxAnnualNetPrice: priceLimit,
          schoolPreferencesNotes: values.notes.trim() || null,
        });
        return true;
      } catch (error: any) {
        toast.error(String(error?.message || "Failed to save"));
        return false;
      }
    });
    return () => registerSave(null);
  }, [registerSave, updateProfile]);

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter(item => item !== value) : [...list, value];
  const chooseLocation = (mode: LocationMode) => {
    setLocationMode(mode);
    if (mode === "anywhere") {
      setStates([]);
      setRegions([]);
    }
    if (mode === "home") {
      setRegions([]);
      setStates(profile.state && NEARBY[profile.state] ? [profile.state] : []);
    }
    if (mode === "nearby") {
      setRegions([]);
      setStates(
        profile.state && NEARBY[profile.state] ? [profile.state, ...NEARBY[profile.state]] : []
      );
    }
    if (mode === "regions") setStates([]);
    if (mode === "specific") {
      setRegions([]);
      if (states.length === 0 && profile.state && NEARBY[profile.state]) setStates([profile.state]);
    }
  };

  return (
    <OnboardingQuestion
      eyebrow="Your search"
      title="What should I look for in a college?"
      description="Start broad. You can fine-tune every preference once you see your matches."
      coachCopy="The best first list leaves room for schools you haven’t discovered yet."
      coachBadge="FIT"
    >
      <div className="space-y-8">
        <SelectionSection label="Which levels would you consider?" help="Choose all that apply.">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {DIVISION_OPTIONS.map(option => (
              <ChoiceCard
                key={option.value}
                selected={divisions.includes(option.value)}
                onClick={() => setDivisions(previous => toggle(previous, option.value))}
                icon={option.icon}
                title={option.title}
                description={option.description}
                multiple
              />
            ))}
          </div>
        </SelectionSection>

        <SelectionSection
          label="Where should I search?"
          help="Anywhere is explicit—there’s no hidden filter when it’s selected."
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              {
                value: "anywhere",
                icon: "∞",
                title: "Anywhere",
                description: "Don’t filter by location",
              },
              {
                value: "home",
                icon: "⌂",
                title: "My home state",
                description: profile.state
                  ? `Prioritize schools in ${profile.state}`
                  : "Prioritize the golfer’s home state",
              },
              {
                value: "nearby",
                icon: "↗",
                title: "Home + nearby states",
                description: "Include states that share a border",
              },
              {
                value: "regions",
                icon: "◫",
                title: "Choose regions",
                description: "Select one or more college-search regions",
              },
              {
                value: "specific",
                icon: "+",
                title: "Choose specific states",
                description: "Build a custom state list",
              },
            ].map(option => (
              <ChoiceCard
                key={option.value}
                selected={locationMode === option.value}
                onClick={() => chooseLocation(option.value as LocationMode)}
                icon={option.icon}
                title={option.title}
                description={option.description}
              />
            ))}
          </div>
          {locationMode === "regions" && (
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {US_REGIONS.map(region => {
                const selected = regions.includes(region.name);
                return (
                  <button
                    key={region.name}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setRegions(previous => toggle(previous, region.name))}
                    className={cn(
                      "rounded-2xl border-2 px-4 py-3 text-left transition-colors",
                      selected
                        ? "border-primary bg-lime-soft text-forest"
                        : "border-border bg-white text-forest hover:border-border-strong"
                    )}
                  >
                    <span className="flex items-center justify-between gap-3">
                      <strong className="text-sm">{region.name}</strong>
                      <Check className={cn("h-4 w-4", selected ? "opacity-100" : "opacity-0")} />
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                      {region.states.join(", ")}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {(locationMode === "specific" || locationMode === "nearby") && (
            <div className="mt-3">
              <StatePicker value={states} onChange={setStates} />
            </div>
          )}
        </SelectionSection>

        <SelectionSection
          label="Optional annual net price limit"
          help="Use what the family could pay after grants and scholarships—not the sticker price."
        >
          <div className="rounded-2xl border-2 border-border bg-white px-5 py-5 shadow-[0_3px_0_#d7e2d9] sm:px-7">
            <div className="mb-5 text-center">
              <span className="block text-[11px] font-extrabold uppercase tracking-[0.12em] text-muted-foreground">
                Maximum per year
              </span>
              <strong className="mt-1 block text-3xl tracking-[-0.04em] text-forest">
                {maxAnnualNetPrice === 0
                  ? "No limit"
                  : `${maxAnnualNetPrice >= 100000 ? "$100,000+" : `$${maxAnnualNetPrice.toLocaleString()}`}`}
              </strong>
            </div>
            <Slider
              aria-label="Maximum annual net price"
              value={[maxAnnualNetPrice]}
              onValueChange={values => setMaxAnnualNetPrice(values[0])}
              min={0}
              max={100000}
              step={5000}
              className="py-2 [&_[role=slider]]:h-7 [&_[role=slider]]:w-7 [&_[role=slider]]:border-forest [&_[role=slider]]:shadow-md [&>span:first-child]:h-3 [&>span:first-child]:bg-muted [&>span:first-child>span]:bg-primary"
            />
            <div className="mt-2 flex justify-between text-[11px] font-bold text-muted-foreground">
              <span>No limit</span>
              <span>$100k+</span>
            </div>
          </div>
        </SelectionSection>

        <SelectionSection
          label="What matters to you in a college?"
          help="Optional. Tell us about size, setting, culture, school type, religious affiliation, or anything else that would make a school feel right."
        >
          <Textarea
            value={notes}
            onChange={event => setNotes(event.target.value.slice(0, 2000))}
            placeholder="Example: A medium-sized school near a city, a collaborative culture, and strong student support matter more than whether it’s public or private."
            className="min-h-28 resize-none rounded-2xl border-border-strong bg-white p-4 text-sm leading-relaxed"
          />
          <p className="mt-1 text-right text-[11px] text-subtle">{notes.length}/2000</p>
        </SelectionSection>
      </div>
    </OnboardingQuestion>
  );
}
